import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { preparerCle } from '../../hors-ligne/synchro.js'
import { accueilHorsLigneVisible } from '../../hors-ligne/visibilite.js'
import { NOM_PLATEFORME, schemaConnexion } from '@afs/shared'
import { Bouton } from '../../../shared/ui/Bouton.js'
import { Champ } from '../../../shared/ui/Champ.js'
import { Bandeau } from '../../../shared/ui/Bandeau.js'
import { estErreurApi } from '../../../shared/api/erreurs.js'
import { connecter } from '../api.js'
import { useOuvrirSession } from '../session.js'
import styles from './Connexion.module.css'

/**
 * Écran A des maquettes — premier écran vu par AFS.
 *
 * Le formulaire est volontairement nu : deux champs, un bouton. Toute
 * décoration ajoutée ici se paie en temps de connexion, plusieurs fois par
 * jour et par agent.
 */
export function Connexion(): React.JSX.Element {
  const naviguer = useNavigate()
  const ouvrirSession = useOuvrirSession()
  const champEmail = useRef<HTMLInputElement>(null)

  // Retour après un changement de mot de passe (Mon compte) : on le dit, et
  // l'adresse est déjà là.
  // Retour d'une session close par le serveur (décision 417) : on dit pourquoi.
  const retour = useLocation().state as {
    motDePasseChange?: unknown
    email?: unknown
    sessionFermee?: unknown
  } | null
  const [email, setEmail] = useState(typeof retour?.email === 'string' ? retour.email : '')
  const [motDePasse, setMotDePasse] = useState('')
  /**
   * Échecs « identifiants invalides » pour cette adresse, comptés ICI. Le
   * serveur répond la même chose pour une adresse inconnue et un mauvais mot
   * de passe — c'est ce qui protège l'annuaire — : il ne dira pas « encore 2
   * essais ». L'écran, lui, sait ce qu'on vient de taper.
   */
  const [echecs, setEchecs] = useState<{ email: string; nombre: number }>({ email: '', nombre: 0 })
  const [erreurSaisie, setErreurSaisie] = useState<{
    email?: string | undefined
    motDePasse?: string | undefined
  }>({})

  // Le champ identifiant a le focus au chargement : l'agent tape sans
  // toucher la souris (skill `frontend-react-afs` § Accessibilité).
  useEffect(() => champEmail.current?.focus(), [])

  const soumission = useMutation({
    mutationFn: connecter,
    onError: (erreurConnexion, saisie) => {
      if (!estErreurApi(erreurConnexion) || erreurConnexion.message !== 'Identifiants invalides.')
        return
      setEchecs((avant) => ({
        email: saisie.email,
        nombre: avant.email === saisie.email ? avant.nombre + 1 : 1,
      }))
    },
    onSuccess: (resultat, saisie) => {
      // CP-12 — le serveur vient d'accepter ce mot de passe : c'est le seul instant où la clé
      // du coffre hors ligne peut se dériver. Les comptes à second facteur n'en ont pas : ils
      // ne se déverrouillent pas hors ligne.
      if (resultat.statut === 'session' || resultat.statut === 'poste_a_declarer') {
        void preparerCle(saisie.email, saisie.motDePasse)
      }
      // Union discriminée : chaque cas est traité explicitement, jamais
      // deviné à partir d'un champ absent (schéma partagé). Le `switch`
      // exhaustif fait échouer la compilation si l'API gagne un statut.
      //
      // `naviguer` rend une promesse depuis React Router 7 : elle est
      // explicitement ignorée, la navigation n'a rien à attendre.
      switch (resultat.statut) {
        case 'second_facteur':
          void naviguer('/connexion/second-facteur', {
            replace: true,
            state: { jetonTemporaire: resultat.jetonTemporaire },
          })
          return

        case 'mot_de_passe_a_changer':
          void naviguer('/connexion/premiere-connexion', {
            replace: true,
            state: { etape: 'mot_de_passe', jetonTemporaire: resultat.jetonTemporaire },
          })
          return

        case 'enrolement_requis':
          void naviguer('/connexion/premiere-connexion', {
            replace: true,
            state: { etape: 'enrolement', jetonTemporaire: resultat.jetonTemporaire },
          })
          return

        case 'poste_a_declarer':
          // Le rôle est rattaché à un site : il déclare où il travaille
          // aujourd'hui avant d'ouvrir sa session (ADR 0002).
          void naviguer('/connexion/poste', {
            replace: true,
            state: { jetonTemporaire: resultat.jetonTemporaire },
          })
          return

        case 'session':
          ouvrirSession(resultat)
          void naviguer('/', { replace: true })
          return
      }
    },
  })

  function soumettre(evenement: React.SyntheticEvent): void {
    evenement.preventDefault()

    // Validation de confort avec le schéma partagé — le serveur revalide,
    // toujours (skill `securite-donnees-sante`).
    const valide = schemaConnexion.safeParse({ email, motDePasse })
    if (!valide.success) {
      const champs = z.flattenError(valide.error).fieldErrors
      setErreurSaisie({ email: champs.email?.[0], motDePasse: champs.motDePasse?.[0] })
      return
    }

    setErreurSaisie({})
    soumission.mutate(valide.data)
  }

  const erreur = soumission.error

  return (
    <div className={styles.ecran}>
      <aside className={styles.marque}>
        <div className={styles.carton}>
          <img src="/marque/logo-afs.png" alt={NOM_PLATEFORME} className={styles.logo} />
        </div>
        {/*
          Le panneau nomme la plateforme, il ne la vend pas. Une promesse
          commerciale s'adresse à un prospect ; ici, celui qui lit est un
          agent d'accueil qui ouvre sa journée, et il la relira des centaines
          de fois. Le seul texte qui lui soit utile est celui d'en dessous.
        */}
        <p className={styles.nomPlateforme}>Plateforme digitale {NOM_PLATEFORME}</p>
        <p className={styles.mention}>
          Accès réservé au personnel autorisé. Toute connexion est journalisée.
        </p>
      </aside>

      <main className={styles.panneau}>
        <form className={styles.formulaire} onSubmit={soumettre} noValidate>
          <img src="/marque/logo-afs.png" alt="" className={styles.logoCompact} />
          {/*
            Le panneau de marque est masqué sous 1024 px, et le logo porte
            encore l'ancien nom dessiné dedans : sans cette ligne, la tablette
            d'accueil ne lirait nulle part le nom de la plateforme.
          */}
          <p className={styles.nomCompact}>Plateforme digitale {NOM_PLATEFORME}</p>

          <div className={styles.entete}>
            <h1 className={styles.titre}>Connexion</h1>
            <p className={styles.sousTitre}>Identifiez-vous pour accéder à la plateforme.</p>
          </div>

          {retour?.motDePasseChange === true && !erreur && (
            <Bandeau ton="succes" titre="Mot de passe changé">
              Toutes vos sessions ont été fermées. Reconnectez-vous avec le nouveau mot de passe.
            </Bandeau>
          )}
          {typeof retour?.sessionFermee === 'string' && !erreur && (
            <Bandeau ton="info" titre={titreSessionFermee(retour.sessionFermee)}>
              Reconnectez-vous pour continuer.
            </Bandeau>
          )}
          {erreur && (
            <Bandeau
              ton="erreur"
              titre={
                estErreurApi(erreur) && erreur.code === 'RESEAU'
                  ? accueilHorsLigneVisible()
                    ? 'Hors ligne'
                    : 'Connexion perdue'
                  : undefined
              }
              reference={estErreurApi(erreur) ? erreur.requestId : undefined}
            >
              {/* Le message vient du serveur — il n'est jamais réécrit ici. */}
              {erreur instanceof Error ? erreur.message : 'Connexion impossible.'}
              {/* Décision 423 : l'entrée vers l'accueil hors ligne est masquée. */}
              {accueilHorsLigneVisible() && estErreurApi(erreur) && erreur.code === 'RESEAU' && (
                <>
                  {' '}
                  {/* L'adresse déjà tapée suit : sans réseau, on ne la redemande pas. */}
                  <Link to="/hors-ligne" state={{ email }}>
                    Ouvrir l’accueil hors ligne
                  </Link>
                </>
              )}
            </Bandeau>
          )}

          {/* Avant le verrouillage, pas après : c'est le seul moment où prévenir sert. */}
          {echecs.nombre >= 2 && echecs.email === email && (
            <Bandeau ton="alerte" titre="Attention au verrouillage">
              Après plusieurs échecs, le compte se verrouille et seul un administrateur peut le
              rouvrir. En cas de doute, passez par{' '}
              <Link to="/mot-de-passe-oublie">Mot de passe oublié&nbsp;?</Link>
            </Bandeau>
          )}

          <Champ
            ref={champEmail}
            libelle="Adresse professionnelle"
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            erreur={erreurSaisie.email}
            onChange={(evenement) => {
              setEmail(evenement.target.value)
            }}
          />

          <Champ
            libelle="Mot de passe"
            type="password"
            name="motDePasse"
            autoComplete="current-password"
            required
            value={motDePasse}
            erreur={erreurSaisie.motDePasse}
            onChange={(evenement) => {
              setMotDePasse(evenement.target.value)
            }}
          />

          <Bouton type="submit" pleineLargeur enCours={soumission.isPending}>
            {soumission.isPending ? 'Connexion…' : 'Se connecter'}
          </Bouton>

          <Link to="/mot-de-passe-oublie" className={styles.lienOubli}>
            Mot de passe oublié&nbsp;?
          </Link>

          {/*
            Sans cette ligne, celui qui n'a pas de compte essaie d'en créer
            un — il n'y a pas d'inscription — puis appelle au hasard. Dire
            qui ouvre les accès évite les deux.
          */}
          <p className={styles.aide}>
            Pas encore de compte&nbsp;? Les accès sont ouverts par l’administrateur de la
            plateforme, à la demande de votre responsable de site.
          </p>
        </form>
      </main>
    </div>
  )
}

/** « Session fermée après 2 h d’inactivité. Reconnectez-vous. » → le titre, sans la consigne. */
export function titreSessionFermee(motif: string): string {
  return motif.replace(/\s*Reconnectez-vous\.?\s*$/, '').replace(/\.$/, '')
}
